import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getRoomInfo, verifyRoomPassword, fetchMessages } from '../services/api';
import { createStompClient, sendMessage, sendLeave } from '../services/websocket';

/* ── Styles ─────────────────────────────────────────── */
const s = {
  page: { height: '100vh', display: 'flex', flexDirection: 'column', background: '#080b10' },

  // Gate (password screen)
  gate: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px', position: 'relative',
  },
  gateGrid: {
    position: 'absolute', inset: 0,
    backgroundImage: `
      linear-gradient(rgba(0,229,255,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,229,255,0.03) 1px, transparent 1px)
    `,
    backgroundSize: '40px 40px', pointerEvents: 'none',
  },
  gateBox: { width: '100%', maxWidth: '380px', position: 'relative', zIndex: 1 },
  gateHeader: { textAlign: 'center', marginBottom: '32px' },
  gateLock: {
    width: '64px', height: '64px', margin: '0 auto 20px',
    background: 'rgba(0,229,255,0.06)',
    border: '1px solid rgba(0,229,255,0.2)',
    borderRadius: '16px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '28px',
  },
  gateTitle: {
    fontFamily: 'Syne, sans-serif', fontSize: '24px', fontWeight: '800',
    color: '#e2eaf3', marginBottom: '4px',
  },
  gateRoomName: { color: '#00e5ff', fontSize: '14px', letterSpacing: '0.05em' },
  gateCard: {
    background: '#0d1117', border: '1px solid #1e2a36',
    borderRadius: '16px', padding: '28px',
  },
  pinRow: { display: 'flex', gap: '10px', marginBottom: '4px', justifyContent: 'center' },
  pinCell: {
    width: '56px', height: '64px',
    background: '#131920', border: '2px solid #1e2a36',
    borderRadius: '10px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '28px', fontFamily: 'JetBrains Mono, monospace',
    fontWeight: '700', color: '#00e5ff',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  pinCellActive: { borderColor: '#00e5ff', boxShadow: '0 0 12px rgba(0,229,255,0.2)' },
  hiddenInput: {
    position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none',
  },

  // Chat
  chatHeader: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 20px',
    background: '#0d1117', borderBottom: '1px solid #1e2a36',
    flexShrink: 0,
  },
  chatTitle: {
    fontFamily: 'Syne, sans-serif', fontSize: '16px', fontWeight: '700', color: '#e2eaf3', flex: 1,
  },
  connBadge: {
    fontSize: '11px', fontFamily: 'JetBrains Mono, monospace',
    display: 'flex', alignItems: 'center', gap: '6px',
    color: '#7a90a4',
  },
  senderTag: {
    fontSize: '11px', letterSpacing: '0.08em',
    background: 'rgba(0,229,255,0.08)', color: '#00e5ff',
    border: '1px solid rgba(0,229,255,0.15)',
    borderRadius: '6px', padding: '3px 10px',
  },

  messages: {
    flex: 1, overflowY: 'auto',
    padding: '20px',
    display: 'flex', flexDirection: 'column', gap: '4px',
  },

  // Message bubbles
  msgRow: (isSelf) => ({
    display: 'flex',
    justifyContent: isSelf ? 'flex-end' : 'flex-start',
    marginBottom: '2px',
  }),
  msgGroup: { maxWidth: '72%' },
  msgSender: {
    fontSize: '11px', color: '#7a90a4', marginBottom: '3px',
    fontFamily: 'JetBrains Mono, monospace',
  },
  msgBubble: (isSelf) => ({
    background: isSelf ? 'rgba(0,145,255,0.15)' : '#131920',
    border: `1px solid ${isSelf ? 'rgba(0,145,255,0.25)' : '#1e2a36'}`,
    borderRadius: isSelf ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
    padding: '10px 14px',
    color: '#e2eaf3',
    fontSize: '14px',
    lineHeight: '1.5',
    wordBreak: 'break-word',
  }),
  msgTime: {
    fontSize: '10px', color: '#3d5166', marginTop: '4px',
    textAlign: 'right', fontFamily: 'JetBrains Mono, monospace',
  },

  // System message
  systemMsg: {
    textAlign: 'center', padding: '6px 0',
    color: '#3d5166', fontSize: '11px',
    letterSpacing: '0.05em',
    fontFamily: 'JetBrains Mono, monospace',
  },

  // Input bar
  inputBar: {
    padding: '16px 20px',
    background: '#0d1117', borderTop: '1px solid #1e2a36',
    display: 'flex', gap: '10px', alignItems: 'flex-end',
    flexShrink: 0,
  },
  textarea: {
    flex: 1, minHeight: '44px', maxHeight: '120px',
    background: '#131920', border: '1px solid #1e2a36',
    borderRadius: '10px', color: '#e2eaf3',
    fontFamily: 'JetBrains Mono, monospace', fontSize: '14px',
    padding: '10px 14px', resize: 'none', outline: 'none',
    transition: 'border-color 0.2s',
    lineHeight: '1.5',
  },
  sendBtn: {
    width: '44px', height: '44px', flexShrink: 0,
    background: '#00e5ff', border: 'none', borderRadius: '10px',
    color: '#080b10', fontSize: '18px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 0 12px rgba(0,229,255,0.2)',
    transition: 'all 0.2s',
  },
};

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/* ── Gate (password entry) ───────────────────────────── */
function PasswordGate({ roomName, onVerified }) {
  const { linkToken } = useParams();
  const [pin, setPin] = useState('');
  const [name, setName] = useState('');
  const [step, setStep] = useState('name'); // 'name' | 'pin'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, [step]);

  const handlePinKey = (e) => {
    if (e.key === 'Backspace') setPin(p => p.slice(0, -1));
    else if (/^\d$/.test(e.key) && pin.length < 4) setPin(p => p + e.key);
  };

  useEffect(() => {
    if (pin.length === 4 && step === 'pin') handleVerify();
  }, [pin]);

  const handleVerify = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await verifyRoomPassword(linkToken, pin, name);
      onVerified(data);
    } catch {
      setError('Wrong PIN. Try again.');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'name') {
    return (
      <div style={s.gate}>
        <div style={s.gateGrid} />
        <div style={s.gateBox} className="fade-up">
          <div style={s.gateHeader}>
            <div style={s.gateLock}>💬</div>
            <h1 style={s.gateTitle}>Joining Room</h1>
            <p style={s.gateRoomName}>{roomName}</p>
          </div>
          <div style={s.gateCard}>
            <label style={{ display: 'block', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#7a90a4', marginBottom: '8px' }}>
              Your Display Name
            </label>
            <input
              className="input"
              ref={inputRef}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Anonymous"
              maxLength={20}
              onKeyDown={e => e.key === 'Enter' && name.trim() && setStep('pin')}
            />
            <p style={{ fontSize: '11px', color: '#3d5166', marginTop: '6px' }}>This is how others will see you</p>
            <button
              className="btn btn-primary"
              style={{ width: '100%', marginTop: '20px', padding: '14px' }}
              disabled={!name.trim()}
              onClick={() => setStep('pin')}
            >
              Continue →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.gate}>
      <div style={s.gateGrid} />
      <div style={s.gateBox} className="fade-up">
        <div style={s.gateHeader}>
          <div style={s.gateLock}>🔒</div>
          <h1 style={s.gateTitle}>Enter PIN</h1>
          <p style={s.gateRoomName}>{roomName}</p>
        </div>
        <div style={s.gateCard}>
          {error && (
            <div style={{ background: 'rgba(255,59,92,0.1)', border: '1px solid rgba(255,59,92,0.3)', borderRadius: '8px', padding: '10px 16px', color: '#ff3b5c', fontSize: '12px', marginBottom: '20px', textAlign: 'center' }}>
              {error}
            </div>
          )}

          {/* Hidden input for keyboard capture */}
          <input
            ref={inputRef}
            style={s.hiddenInput}
            onKeyDown={handlePinKey}
            readOnly
            autoFocus
          />

          {/* PIN display */}
          <div style={s.pinRow} onClick={() => inputRef.current?.focus()}>
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                style={{ ...s.pinCell, ...(i === pin.length ? s.pinCellActive : {}) }}
              >
                {pin[i] ? '•' : ''}
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', fontSize: '11px', color: '#3d5166', marginTop: '12px' }}>
            {loading ? 'Verifying...' : 'Tap above and enter 4-digit PIN'}
          </p>

          <button
            className="btn btn-ghost"
            style={{ width: '100%', marginTop: '20px', fontSize: '11px' }}
            onClick={() => { setStep('name'); setPin(''); setError(''); }}
          >
            ← Change Name
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Chat UI ─────────────────────────────────────────── */
function ChatUI({ roomName, roomToken, linkToken, senderName }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const clientRef = useRef(null);
  const bottomRef = useRef(null);

  // Load history
  useEffect(() => {
    fetchMessages(linkToken, roomToken)
      .then(({ data }) => setMessages(data))
      .catch(console.error);
  }, [linkToken, roomToken]);

  // WebSocket
  useEffect(() => {
    const client = createStompClient(
      roomToken, linkToken,
      (msg) => {
        if (msg.type === 'CLEAR') {
          setMessages([]);
        } else {
          setMessages(prev => [...prev, msg]);
        }
      },
      () => setConnected(true),
      () => setConnected(false),
    );
    client.activate();
    clientRef.current = client;

    return () => {
      sendLeave(client, linkToken);
      client.deactivate();
    };
  }, [roomToken, linkToken]);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || !connected) return;
    sendMessage(clientRef.current, linkToken, text);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.chatHeader}>
        <span style={{ fontSize: '18px' }}>🔐</span>
        <span style={s.chatTitle}>{roomName}</span>
        <span style={s.senderTag}>{senderName}</span>
        <div style={s.connBadge}>
          <span className="pulse-dot" style={{ background: connected ? '#00e676' : '#ff3b5c' }} />
          {connected ? 'Live' : 'Reconnecting…'}
        </div>
      </div>

      {/* Messages */}
      <div style={s.messages}>
        {messages.length === 0 && (
          <div style={{ ...s.systemMsg, marginTop: '40px' }}>
            No messages yet. Say something!
          </div>
        )}
        {messages.map((msg, i) => {
          if (msg.type === 'JOIN' || msg.type === 'LEAVE' || msg.senderName === 'System') {
            return <div key={i} style={s.systemMsg}>{msg.content}</div>;
          }
          const isSelf = msg.senderName === senderName;
          return (
            <div key={msg.id ?? i} style={s.msgRow(isSelf)}>
              <div style={s.msgGroup}>
                {!isSelf && <div style={s.msgSender}>{msg.senderName}</div>}
                <div style={s.msgBubble(isSelf)}>{msg.content}</div>
                <div style={s.msgTime}>{formatTime(msg.sentAt)}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={s.inputBar}>
        <textarea
          style={s.textarea}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={connected ? 'Type a message…' : 'Connecting…'}
          disabled={!connected}
          rows={1}
        />
        <button
          style={{ ...s.sendBtn, opacity: (!input.trim() || !connected) ? 0.4 : 1 }}
          onClick={handleSend}
          disabled={!input.trim() || !connected}
        >
          ➤
        </button>
      </div>
    </div>
  );
}

/* ── Main ChatRoom page (orchestrator) ───────────────── */
export default function ChatRoom() {
  const { linkToken } = useParams();
  const [roomName, setRoomName] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [access, setAccess] = useState(null); // { roomToken, senderName }

  useEffect(() => {
    getRoomInfo(linkToken)
      .then(({ data }) => setRoomName(data.name))
      .catch(() => setNotFound(true));
  }, [linkToken]);

  if (notFound) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
        <div style={{ fontSize: '40px' }}>🚫</div>
        <p style={{ color: '#7a90a4', fontFamily: 'JetBrains Mono, monospace' }}>Room not found or inactive.</p>
      </div>
    );
  }

  if (!roomName) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#3d5166', fontFamily: 'JetBrains Mono, monospace' }}>Loading room...</p>
      </div>
    );
  }

  if (!access) {
    return (
      <PasswordGate
        roomName={roomName}
        onVerified={(data) => setAccess({ roomToken: data.roomToken, senderName: data.senderName })}
      />
    );
  }

  return (
    <ChatUI
      roomName={roomName}
      roomToken={access.roomToken}
      linkToken={linkToken}
      senderName={access.senderName}
    />
  );
}
